import api from './api';

const fileListCache = new Map<string, { ts: number; data: any }>();
const fileListInflight = new Map<string, Promise<any>>();
const FILE_LIST_TTL_MS = 5000;

export const uploadStageFile = async (
  agencySlug: string,
  propertyId: string,
  stageIndex: number,
  file: File,
  workflowId: string,
  fileType?: string,
  options?: { visitorExposable?: boolean }
) => {
  const formData = new FormData();
  formData.append('file', file);
  if (fileType) formData.append('type', fileType);
  if (options?.visitorExposable !== undefined) {
    formData.append('visitorExposable', String(options.visitorExposable));
  }
  const url = `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`;
  const res = await api.post(url, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return res.data;
};

export const listStageFiles = async (agencySlug: string, propertyId: string, stageIndex: number, workflowId: string) => {
  // Property-level list (stage/workflow ignored by backend)
  const key = `${agencySlug}|${propertyId}|${workflowId}|${stageIndex}`;
  const now = Date.now();
  const cached = fileListCache.get(key);
  if (cached && now - cached.ts < FILE_LIST_TTL_MS) {
    return cached.data;
  }
  const inflight = fileListInflight.get(key);
  if (inflight) {
    return inflight;
  }
  const url = `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files`;
  const request = api.get(url).then((res) => {
    fileListCache.set(key, { ts: Date.now(), data: res.data });
    fileListInflight.delete(key);
    return res.data;
  }).catch((err) => {
    fileListInflight.delete(key);
    throw err;
  });
  fileListInflight.set(key, request);
  return request;
};

export const downloadStageFile = (agencySlug: string, propertyId: string, stageIndex: number, fileId: string, workflowId: string) => {
  // Prefer generic by-id endpoint to keep storage opaque
  const url = `/api/agencies/${agencySlug}/files/${fileId}`;
  return url;
};

export const downloadFileAsBlob = async (downloadUrl: string, suggestedName: string) => {
  const token = localStorage.getItem('token');
  // Do not send Authorization to presigned storage URLs (OCI/S3) - they are self-contained and may return 400 if given unexpected headers
  const isPresignedExternalUrl = downloadUrl.startsWith('http://') || downloadUrl.startsWith('https://');
  const headers: HeadersInit = {};
  if (token && !isPresignedExternalUrl) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(downloadUrl, { headers });
  if (!response.ok) throw new Error('Download failed');
  const blob = await response.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = suggestedName || 'download';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
};

export const deleteStageFile = async (agencySlug: string, propertyId: string, stageIndex: number, fileId: string, workflowId: string) => {
  // Property-level delete (stage/workflow ignored by backend)
  const url = `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageIndex}/files/${fileId}`;
  const res = await api.delete(url);
  return res.data;
};

export const updateStageFileType = async (
  agencySlug: string,
  propertyId: string,
  stageId: string | number,
  fileId: string,
  type: string,
  workflowId: string
) => {
  const url = `/agencies/${agencySlug}/properties/${propertyId}/workflows/${workflowId}/stages/${stageId}/files/${fileId}/type`;
  const res = await api.patch(url, { type });
  return res.data;
};